import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Compass, Save, Info, Filter, ShoppingBag, KeyRound, Ticket, ShieldCheck, BookUser, ClockArrowUp, Cpu } from "lucide-react";

const ICONS = { ShoppingBag, KeyRound, Ticket, ShieldCheck, Info, BookUser, ClockArrowUp, Cpu, Compass };

/**
 * UseCaseMapper, admin view that lets you control, per use-case ("Gebruikssituatie"),
 * which software modules a customer sees during configuration.
 *
 * Mental model the user gave us:
 *   Customer step "what will the kiosk be used for?" → Use-case (e.g. Ordering & Payment, Check-in)
 *   → Show only the software relevant to that use-case
 *   → Don't show BEX/Recranet when use-case = ordering, etc.
 *
 * Driven by the existing `SoftwareModule.usecase_slugs` field. Editing here updates that array.
 */
export default function UseCaseMapper() {
  const [usecases, setUsecases] = useState([]);
  const [software, setSoftware] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [drafts, setDrafts] = useState({}); // softwareId -> usecase_slugs[]
  const [saving, setSaving] = useState(false);
  const [lang] = useState("nl");
  const [sectorFilter, setSectorFilter] = useState("");

  const load = async () => {
    const [ucRes, swRes, secRes, pRes] = await Promise.all([
      api.get("/usecases"),
      api.get("/admin/software"),
      api.get("/sectors"),
      api.get("/admin/partners"),
    ]);
    const ucs = (ucRes.data || []).slice().sort((a, b) => (a.order || 99) - (b.order || 99));
    setUsecases(ucs);
    setSoftware(swRes.data || []);
    setSectors(secRes.data || []);
    setPartners(pRes.data || []);
    // Build initial drafts: { swId: [...usecase_slugs] }
    const d = {};
    (swRes.data || []).forEach((s) => { d[s.id] = [...(s.usecase_slugs || [])]; });
    setDrafts(d);
  };

  useEffect(() => { load(); }, []);

  const dirtyIds = useMemo(() => {
    return software
      .filter((s) => JSON.stringify((s.usecase_slugs || []).slice().sort()) !== JSON.stringify((drafts[s.id] || []).slice().sort()))
      .map((s) => s.id);
  }, [software, drafts]);

  const toggle = (swId, ucSlug) => {
    setDrafts((d) => {
      const cur = new Set(d[swId] || []);
      if (cur.has(ucSlug)) cur.delete(ucSlug); else cur.add(ucSlug);
      return { ...d, [swId]: Array.from(cur) };
    });
  };

  const saveAll = async () => {
    if (dirtyIds.length === 0) return;
    setSaving(true);
    try {
      const tasks = dirtyIds.map((id) => {
        const sw = software.find((s) => s.id === id);
        return api.put(`/admin/software/${id}`, { ...sw, usecase_slugs: drafts[id] });
      });
      const results = await Promise.all(tasks);
      setSoftware((cur) => cur.map((s) => results.find((r) => r.data.id === s.id)?.data || s));
      toast.success(`${dirtyIds.length} software-module(s) opgeslagen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  // For each (usecase, software) pair: is the software currently mapped?
  const mapped = (swId, slug) => (drafts[swId] || []).includes(slug);

  // Per software, which sectors would actually expose it (intersection of software.sector_slugs)
  const swSectorLabels = (sw) => {
    if (!sw.sector_slugs?.length) return "alle sectoren";
    return sw.sector_slugs.map((sl) => sectors.find((s) => s.slug === sl)?.name_i18n?.nl || sl).join(", ");
  };

  // Filter software list by sector if active
  const visibleSoftware = useMemo(() => {
    if (!sectorFilter) return software;
    return software.filter((s) => !s.sector_slugs?.length || s.sector_slugs.includes(sectorFilter));
  }, [software, sectorFilter]);

  // Count partners that would actually expose this software (informational tag)
  const partnerExposure = (sw) => {
    let total = 0, allowed = 0;
    partners.forEach((p) => {
      if (!p.landing_enabled) return;
      total += 1;
      const a = p.allowed_software_slugs;
      if (!a || a.length === 0 || a.includes(sw.slug)) allowed += 1;
    });
    return { total, allowed };
  };

  return (
    <div className="space-y-5" data-testid="usecase-mapper">
      <div className="card-surface p-5">
        <div className="flex items-start gap-3">
          <Compass className="w-5 h-5 text-[#D97742] mt-0.5 shrink-0" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-white">Gebruikssituaties → Software-zichtbaarheid</h2>
            <p className="text-xs text-[#A8B3AD] mt-1 max-w-3xl">
              In de configurator vraagt de klant zich af: <em>"Waarvoor wil ik de kiosk gebruiken?"</em>. Per gebruikssituatie bepaal je hier welke software-modules klanten zien. Bijvoorbeeld: bij <strong>Bestellen &amp; Betalen</strong> hoef je geen BEX of Recranet PMS te tonen.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              data-testid="usecase-sector-filter"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="px-3 py-1.5 rounded bg-[#0F1816] border border-[#1F3A2E] text-sm text-white"
            >
              <option value="">Alle sectoren</option>
              {sectors.map((s) => <option key={s.slug} value={s.slug}>{s.name_i18n?.nl || s.slug}</option>)}
            </select>
            <button
              data-testid="usecase-save-btn"
              onClick={saveAll}
              disabled={dirtyIds.length === 0 || saving}
              className={`flex items-center gap-2 px-4 py-1.5 rounded text-sm font-semibold transition ${dirtyIds.length > 0 ? "bg-[#D97742] text-white hover:bg-[#c46939]" : "bg-[#1F3A2E] text-[#5d6a64] cursor-not-allowed"}`}
            >
              <Save className="w-4 h-4" /> {saving ? "Opslaan…" : dirtyIds.length > 0 ? `Opslaan (${dirtyIds.length})` : "Opgeslagen"}
            </button>
          </div>
        </div>
      </div>

      {/* Use-case columns */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {usecases.map((uc) => {
          const Icon = ICONS[uc.icon] || Compass;
          const linked = visibleSoftware.filter((s) => (drafts[s.id] || []).includes(uc.slug));
          const others = visibleSoftware.filter((s) => !(drafts[s.id] || []).includes(uc.slug));
          return (
            <div key={uc.id} className="card-surface p-4" data-testid={`usecase-card-${uc.slug}`}>
              <div className="flex items-start gap-3 mb-3 pb-3 border-b border-[#1F3A2E]">
                <div className="w-9 h-9 rounded-lg bg-[#D97742]/15 border border-[#D97742]/30 flex items-center justify-center shrink-0">
                  <Icon className="w-4.5 h-4.5 text-[#D97742]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-white">{uc.name_i18n?.[lang] || uc.slug}</h3>
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#1F3A2E] text-[#7FD8B8]">{uc.slug}</span>
                  </div>
                  <p className="text-[11px] text-[#A8B3AD] mt-0.5 line-clamp-1">{uc.description_i18n?.[lang]}</p>
                </div>
                <span className="text-[11px] text-[#A8B3AD] shrink-0">{linked.length} gekoppeld</span>
              </div>

              {/* Linked software */}
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-[#7FD8B8] mb-1.5 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#7FD8B8]" /> Wordt getoond bij deze gebruikssituatie
                </div>
                {linked.length === 0 && (
                  <div className="text-[11px] text-[#5d6a64] italic px-3 py-2">Nog niets gekoppeld, voeg modules toe ↓</div>
                )}
                {linked.map((sw) => {
                  const exp = partnerExposure(sw);
                  return (
                    <button
                      key={sw.id}
                      data-testid={`uc-${uc.slug}-sw-${sw.slug}-on`}
                      onClick={() => toggle(sw.id, uc.slug)}
                      className="w-full text-left px-3 py-2 rounded border bg-[#1F3A2E]/40 border-[#7FD8B8]/40 hover:border-[#D97742]/40 transition flex items-center gap-2.5"
                    >
                      <div className="w-4 h-4 rounded border bg-[#7FD8B8] border-[#7FD8B8] flex items-center justify-center shrink-0">
                        <span className="text-[#0a1410] text-[10px] font-bold">✓</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{sw.name}</div>
                        <div className="text-[10px] text-[#A8B3AD] flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono">{sw.slug}</span>
                          <span>· €{Number(sw.price_monthly || 0).toFixed(0)}/mnd</span>
                          {exp.total > 0 && <span>· zichtbaar bij {exp.allowed}/{exp.total} partners</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Available to link */}
              {others.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1F3A2E]">
                  <div className="text-[10px] uppercase tracking-wider text-[#5d6a64] mb-1.5 flex items-center gap-1">
                    <Filter className="w-2.5 h-2.5" /> Klik om toe te voegen aan deze gebruikssituatie
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {others.map((sw) => (
                      <button
                        key={sw.id}
                        data-testid={`uc-${uc.slug}-sw-${sw.slug}-off`}
                        onClick={() => toggle(sw.id, uc.slug)}
                        className="px-2 py-1 rounded-full text-[11px] border border-[#1F3A2E] text-[#A8B3AD] hover:border-[#D97742]/50 hover:text-white transition"
                      >
                        + {sw.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="card-surface p-4 text-xs text-[#A8B3AD]">
        <p className="flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#7FD8B8]" />
          <span>
            <strong className="text-white">Hoe werkt de filtering in de configurator?</strong> Eerst wordt gefilterd op <em>sector</em> (Hospitality, Leisure, …), software krijgt alleen mensen te zien als de sector matcht. Daarna op <em>gebruikssituatie</em> die je hier beheert. Als laatste filteren partners zoals BEX/Tommy hun catalogus via <span className="font-mono text-[#7FD8B8]">allowed_software_slugs</span> op hun eigen landing. Drie lagen, alle drie zichtbaar en aanpasbaar.
          </span>
        </p>
      </div>
    </div>
  );
}
